// src/context/AuthContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { db, auth, paths } from "../firebase/config";
import {
  signInAnonymously,
  onAuthStateChanged,
  signInWithCustomToken,
} from "firebase/auth";
import {
  collection,
  query,
  where,
  getDocs,
  setDoc,
  doc,
  addDoc,
  serverTimestamp,
  updateDoc,
} from "firebase/firestore";
import { limparCPF } from "../utils/helpers";

export const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);

export const ADMIN_SEED_CPF = "11122233344";

// NOVO: 30 minutos em milissegundos para timeout de inatividade
const SESSION_TIMEOUT_MS = 7200000; 

let timeoutHandler; // Manipulador de timeout global

export const logAuditoria = async (user, acao, payload = {}) => {
  try {
    await addDoc(collection(db, paths.logs), {
      usuario_id: user?.id || "sistema",
      usuario_nome: user?.nome || "Sistema",
      acao,
      payload: JSON.stringify(payload),
      criado_em: serverTimestamp(),
    });
  } catch (e) {
    console.error("Erro ao registrar log:", e);
  }
};

export const AuthProvider = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null); // Perfil (Admin, Escalante, Cadete)
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [isAdminCreated, setIsAdminCreated] = useState(null); // null = checando, false = não existe, true = existe
  // NOVO: Estado para expiração de sessão
  const [sessionExpired, setSessionExpired] = useState(false); 

  const createAdminUser = async () => {
    try {
      console.log("Criando Admin Mestre...");
      const adminRef = doc(collection(db, paths.users)); // Gera novo ID
      const newAdminProfile = {
        id: adminRef.id,
        cpf: ADMIN_SEED_CPF,
        nome: "Maj PM EO (Admin)",
        nivel: "admin",
        pelotao_id: null,
        senha: ADMIN_SEED_CPF,
      };

      await setDoc(adminRef, newAdminProfile); // Salva no banco
      await logAuditoria(newAdminProfile, "Criação automática do Admin Mestre");

      setUserProfile(newAdminProfile);
      setIsAdminCreated(true);

      console.log("Admin Mestre criado e logado.");
      return true;
    } catch (error) {
      console.error("Erro ao criar Admin Mestre:", error);
      return false;
    }
  };

  // NOVO: Função para redefinir o timeout
  const resetSessionTimeout = useCallback(() => {
    if (userProfile && !sessionExpired) {
      clearTimeout(timeoutHandler);
      timeoutHandler = setTimeout(() => {
        setSessionExpired(true);
      }, SESSION_TIMEOUT_MS);
    }
  }, [userProfile, sessionExpired]);

  // NOVO: Efeito para configurar listeners de atividade
  useEffect(() => {
    const events = ['click', 'mousemove', 'keypress', 'scroll', 'touchstart'];
    
    if (userProfile) {
      events.forEach(event => window.addEventListener(event, resetSessionTimeout));
      resetSessionTimeout();
      
      return () => {
        clearTimeout(timeoutHandler);
        events.forEach(event => window.removeEventListener(event, resetSessionTimeout));
      };
    }
    
    // Limpa se não houver userProfile
    clearTimeout(timeoutHandler);
    setSessionExpired(false);
    
    return () => clearTimeout(timeoutHandler);
  }, [userProfile, resetSessionTimeout]);


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setFirebaseUser(user);

        if (isAdminCreated === null && !userProfile) {
          // *** BYPASS TEMPORÁRIO ***: Assumir que o admin existe para pular a tela de setup.
          setIsAdminCreated(true); 
        }

        if (!userProfile) {
          setLoadingAuth(false);
        }
      } else {
        try {
          if (
            typeof __initial_auth_token !== "undefined" &&
            __initial_auth_token
          ) {
            await signInWithCustomToken(auth, __initial_auth_token);
          } else {
            await signInAnonymously(auth);
          }
        } catch (error) {
          console.error("Falha no login automático:", error);
          setLoadingAuth(false);
        }
      }
    });

    // Se a sessão expirou, força o logout e limpa o state
    if (sessionExpired && userProfile) {
        logoutApp(true); // Chama logout com aviso
        setSessionExpired(false);
    }

    return () => unsubscribe();
  }, [userProfile, isAdminCreated, sessionExpired]); // Adicionado sessionExpired

  const loginApp = async (cpf, senha) => {
    const cpfLimpo = limparCPF(cpf);
    if (cpfLimpo.length !== 11) {
      throw new Error("CPF deve conter 11 dígitos.");
    }
    if (!senha) {
      throw new Error("Senha é obrigatória.");
    }

    const q = query(collection(db, paths.users), where("cpf", "==", cpfLimpo));
    try {
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        throw new Error("CPF ou senha incorreta.");
      }

      const userDoc = snapshot.docs[0];
      const profileData = { id: userDoc.id, ...userDoc.data() };

      if (profileData.senha !== senha) {
        await logAuditoria(
          profileData,
          "Tentativa de login (Senha inválida)"
        );
        throw new Error("CPF ou senha incorreta.");
      }

      setUserProfile(profileData);
      setLoadingAuth(false);

      await logAuditoria(profileData, "Login efetuado");
      resetSessionTimeout(); // Inicia o timeout após o login

      return profileData;
    } catch (error) {
      console.error("Erro no loginApp:", error);
      throw error;
    }
  };

  // MODIFICADO: Adiciona o parâmetro showWarning
  const logoutApp = (showWarning = false) => {
    setUserProfile(null);
    setIsAdminCreated(null);
    setLoadingAuth(true);
    clearTimeout(timeoutHandler); // Limpa o handler

    // Se for um logout por timeout, armazena um aviso no localStorage
    if (showWarning) {
        localStorage.setItem('sessionTimeoutWarning', 'true');
    }
  };

  const value = {
    firebaseUser,
    userProfile,
    loadingAuth,
    isAdminCreated,
    createAdminUser,
    loginApp,
    logoutApp,
    isAdmin: userProfile?.nivel === "admin",
    isEscalante: userProfile?.nivel === "escalante",
    isCadete: userProfile?.nivel === "cadete",
    isGerenciador: userProfile?.nivel === "gerenciador",
    isCadeteAcademico: userProfile?.nivel === "cadete_academico",
    isSoe: userProfile?.nivel === "soe",
    isCmtPelotao: userProfile?.nivel === "cmt_pelotao", // NOVO NÍVEL ADICIONADO
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};